import React, { useEffect, useMemo, useState, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { formatUSD } from "@/lib/utils";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Plus,
  Pencil,
  Loader2,
  Search,
  Send,
  KeyRound,
  Eye,
  Power,
  X,
  MoreHorizontal,
  ArrowDown,
  Crown,
  Banknote,
  HandCoins,
  Percent,
  Shuffle,
  IdCard,
} from "lucide-react";
import ChangeRoleModal from "@/components/ChangeRoleModal";
import IdentityDocumentModal from "@/components/IdentityDocumentModal";
import CountryPhoneFields from "@/components/CountryPhoneFields";
import PasswordInput from "@/components/ui/PasswordInput";

const inputCls =
  "flex h-10 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE] focus:border-transparent";

const PAGE_SIZES = [10, 25, 50];

export default function MyAgents() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const masterFilterId = searchParams.get("master") || "";
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "MASTER_ADMIN";
  const [agents, setAgents] = useState([]);
  const [agentsTotal, setAgentsTotal] = useState(0);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [masterAgents, setMasterAgents] = useState([]);
  const [masterInfo, setMasterInfo] = useState(null); // viewing-context info when ?master=<id>
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const [showCreate, setShowCreate] = useState(() => searchParams.get("action") === "create");
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [cashing, setCashing] = useState(null); // Transfer Cash target
  const [cashTargetWallet, setCashTargetWallet] = useState(null);
  const [cashAmount, setCashAmount] = useState("");
  const [cashNote, setCashNote] = useState("");
  const [cashWithdrawing, setCashWithdrawing] = useState(null);
  const [cashWithdrawWallet, setCashWithdrawWallet] = useState(null);
  const [cashWithdrawAmount, setCashWithdrawAmount] = useState("");
  const [changingRole, setChangingRole] = useState(null);
  const [idDocFor, setIdDocFor] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState(blankForm());
  const [editForm, setEditForm] = useState({});
  const [resetPwd, setResetPwd] = useState("");
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");

  const load = useCallback(async () => {
    setLoadingAgents(true);
    try {
      const params = new URLSearchParams({
        limit: String(pageSize),
        skip: String((page - 1) * pageSize),
      });
      if (debouncedSearch) params.set("q", debouncedSearch);
      if (isAdmin && masterFilterId) params.set("master_id", masterFilterId);
      const { data } = await api.get(`/agents?${params.toString()}`);
      // Backend always returns paginated shape now
      const items = Array.isArray(data) ? data : (data.items || []);
      const total = Array.isArray(data) ? items.length : (data.total ?? items.length);
      setAgents(items);
      setAgentsTotal(total);
    } finally {
      setLoadingAgents(false);
    }
  }, [page, pageSize, debouncedSearch, isAdmin, masterFilterId]);

  // Fetch the master-agent dropdown once for admins (used inside Create modal
  // and elsewhere). Kept in its own effect so the main `load` doesn't have to
  // depend on `masterAgents.length` (which would create an extra fetch loop).
  useEffect(() => {
    if (!isAdmin) return;
    let mounted = true;
    api.get("/master-agents").then(({ data }) => {
      if (mounted) setMasterAgents(data);
    }).catch(() => { /* ignore */ });
    return () => { mounted = false; };
  }, [isAdmin]);

  // When ?master=<id> changes, fetch the master's profile info for the filter chip
  useEffect(() => {
    if (!isAdmin || !masterFilterId) { setMasterInfo(null); return; }
    let mounted = true;
    api.get("/master-agents").then(({ data }) => {
      if (!mounted) return;
      const m = (data || []).find((x) => x.id === masterFilterId);
      setMasterInfo(m || { id: masterFilterId, username: "(unknown)", name: "" });
    }).catch(() => mounted && setMasterInfo({ id: masterFilterId, username: "(unknown)", name: "" }));
    return () => { mounted = false; };
  }, [isAdmin, masterFilterId]);

  const clearMasterFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("master");
    setSearchParams(next, { replace: true });
  };
  // Debounce search input → backend
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { load(); }, [load]);

  // Server-paginated rows: just display whatever the backend returned.
  const pageRows = agents;
  const filtered = { length: agentsTotal };
  const totalPages = Math.max(1, Math.ceil(agentsTotal / pageSize));
  const pageStart = (page - 1) * pageSize;

  const create = async (e) => {
    e.preventDefault();
    setCreateError("");
    setBusy(true);
    try {
      const { country_code, ...agentPayload } = form;
      const { data: created } = await api.post("/agents", agentPayload);
      // Apply the country selection right after creation (backend enforces
      // the phone-country mismatch guard). If the admin chose to leave the
      // country blank we skip this — they can set it later via Edit.
      if (country_code && created?.id) {
        try {
          await api.patch(`/admin/users/${created.id}/country`, { country_code });
        } catch (err) {
          if (err.response?.status === 409) {
            const confirmed = window.confirm(
              (err.response.data?.detail || "") +
              "\n\nSave the agent with this mismatched country anyway?",
            );
            if (confirmed) {
              await api.patch(`/admin/users/${created.id}/country`, {
                country_code, mismatch_confirmed: true,
              });
            }
          } else {
            throw err;
          }
        }
      }
      toast.success(t("ui.agent_created"));
      setShowCreate(false);
      setForm(blankForm());
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || t("ui.create_agent_failed");
      setCreateError(typeof msg === "string" ? msg : t("ui.create_agent_failed"));
    } finally { setBusy(false); }
  };

  const startEdit = (a) => {
    setEditError("");
    setEditing(a.id);
    setEditForm({
      name: a.name || "",
      username: a.username || "",
      email: a.email || "",
      phone: a.phone || "",
      country_code: a.country_code || null,
      commission_rate: a.commission_rate ?? 0,
      active: !!a.active,
    });
  };

  const saveEdit = async () => {
    setEditError("");
    setBusy(true);
    try {
      // Country changes go through the dedicated endpoint so the backend
      // enforces the phone-country mismatch guard, records USER_COUNTRY_UPDATE,
      // and (spec §5) preserves every existing wallet balance.
      const currentAgent = agents.find((x) => x.id === editing);
      const nextCountry = editForm.country_code || null;
      const priorCountry = currentAgent?.country_code || null;
      const countryChanged = nextCountry && nextCountry !== priorCountry;

      // Strip country_code from the regular PATCH payload — it's owned by
      // the country endpoint.
      const { country_code: _cc, ...rest } = editForm;
      await api.patch(`/agents/${editing}`, rest);

      if (countryChanged) {
        try {
          await api.patch(`/admin/users/${editing}/country`, { country_code: nextCountry });
        } catch (err) {
          if (err.response?.status === 409) {
            // Mismatch — prompt the admin to confirm.
            const confirmed = window.confirm(
              (err.response.data?.detail || "") +
              "\n\nProceed anyway with this mismatched combination?"
            );
            if (!confirmed) throw err;
            await api.patch(`/admin/users/${editing}/country`, {
              country_code: nextCountry,
              mismatch_confirmed: true,
            });
          } else {
            throw err;
          }
        }
      }
      toast.success(t("ui.agent_updated"));
      setEditing(null);
      load();
    } catch (err) {
      const msg = err.response?.data?.detail || t("ui.update_failed");
      setEditError(typeof msg === "string" ? msg : t("ui.update_failed"));
    } finally { setBusy(false); }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/agents/${a.id}`, { active: !a.active });
      toast.success(a.active ? t("ui.agent_inactive") : t("ui.agent_active"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ui.failed_generic"));
    }
  };

  const [resetActorPwd, setResetActorPwd] = useState("");
  const submitReset = async () => {
    // Client-side sanity checks — the real policy lives on the backend.
    if (!resetPwd || resetPwd.length < 8) {
      toast.error(t("ui.pw_min_8", { defaultValue: "New password must be at least 8 characters." }));
      return;
    }
    if (!/[A-Za-z]/.test(resetPwd) || !/[0-9]/.test(resetPwd) || /\s/.test(resetPwd)) {
      toast.error(t("ui.pw_policy", { defaultValue: "Password needs at least one letter, one digit and no spaces." }));
      return;
    }
    if (!resetActorPwd) {
      toast.error(t("ui.confirm_own_pw", { defaultValue: "Please confirm your own password to authorise this change." }));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/agents/${resetting.id}/reset-password`, {
        password: resetPwd,
        actor_password: resetActorPwd,
      });
      toast.success(t("ui.pw_reset_for", { username: resetting.username }));
      setResetting(null);
      setResetPwd("");
      setResetActorPwd("");
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ui.pw_reset_failed"));
    } finally { setBusy(false); }
  };

  const openSales = async (a) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/agents/${a.id}/sales?limit=50`);
      setViewing({ ...a, ...data });
    } catch (e) {
      toast.error(t("ui.could_not_load"));
    } finally { setBusy(false); }
  };

  const goTransfer = (a) => navigate(`/transfer-funds?agent=${a.id}`);

  const submitWithdraw = async () => {
    const amt = parseFloat(withdrawAmount);
    if (!amt || amt <= 0) { toast.error(t("ui.enter_valid_amount")); return; }
    if (amt > Number(withdrawing.balance_usd || 0)) {
      toast.error(t("ui.only_agent_has", { amount: formatUSD(withdrawing.balance_usd) }));
      return;
    }
    setBusy(true);
    try {
      await api.post(`/agents/${withdrawing.id}/withdraw`, { amount: amt, note: null });
      toast.success(t("ui.withdraw_success", { amount: formatUSD(amt), username: withdrawing.username }));
      setWithdrawing(null);
      setWithdrawAmount("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ui.withdraw_failed"));
    } finally { setBusy(false); }
  };

  const openTransferCash = async (a) => {
    setCashing(a);
    setCashAmount("");
    setCashNote("");
    setCashTargetWallet(null);
    try {
      const { data } = await api.get(`/cash-wallet/${a.id}`);
      setCashTargetWallet(data);
    } catch {
      setCashTargetWallet({ currency: "USD", balance: 0, name: a.name, username: a.username });
    }
  };

  const submitTransferCash = async () => {
    const amt = parseFloat(cashAmount);
    if (!amt || amt <= 0) { toast.error(t("ui.enter_valid_amount")); return; }
    setBusy(true);
    try {
      const body = {
        agent_id: cashing.id,
        amount: amt,
        currency: cashTargetWallet?.currency || undefined,
        note: cashNote.trim() || null,
      };
      await api.post(`/cash-transfers`, body);
      toast.success(t("ui.cash_transfer_success", { amount: amt, currency: cashTargetWallet?.currency || "", username: cashing.username }));
      setCashing(null);
      setCashAmount("");
      setCashNote("");
      setCashTargetWallet(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ui.cash_transfer_failed"));
    } finally { setBusy(false); }
  };

  const openWithdrawCash = async (a) => {
    setCashWithdrawing(a);
    setCashWithdrawAmount("");
    setCashWithdrawWallet(null);
    try {
      const { data } = await api.get(`/cash-wallet/${a.id}`);
      setCashWithdrawWallet(data);
    } catch {
      setCashWithdrawWallet({ currency: "USD", balance: 0, name: a.name, username: a.username });
    }
  };

  const submitWithdrawCash = async () => {
    const amt = parseFloat(cashWithdrawAmount);
    if (!amt || amt <= 0) { toast.error(t("ui.enter_valid_amount")); return; }
    if (cashWithdrawWallet && amt > Number(cashWithdrawWallet.balance || 0)) {
      toast.error(t("ui.only_available", { amount: cashWithdrawWallet.balance, currency: cashWithdrawWallet.currency }));
      return;
    }
    setBusy(true);
    try {
      await api.post("/cash-withdrawals", {
        user_id: cashWithdrawing.id,
        amount: amt,
        currency: cashWithdrawWallet?.currency || undefined,
      });
      toast.success(t("ui.cash_withdraw_success", { amount: amt, currency: cashWithdrawWallet?.currency || "", username: cashWithdrawing.username }));
      setCashWithdrawing(null);
      setCashWithdrawAmount("");
      setCashWithdrawWallet(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("ui.cash_withdraw_failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="agents-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
            {isAdmin && masterFilterId ? t("ui.network") : t("agents.title")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {isAdmin && masterFilterId && masterInfo
              ? `${t("agents.title")} · ${masterInfo.name || masterInfo.username}`
              : t("agents.title")}
          </h1>
          {isAdmin && masterFilterId && masterInfo && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <span
                data-testid="master-filter-chip"
                className="inline-flex items-center gap-2 h-8 px-3 rounded-full bg-violet-50 text-violet-700 border border-violet-200 text-xs font-semibold"
              >
                <Crown className="w-3.5 h-3.5" />
                {t("ui.master_agent_label")}: <span className="font-bold">{masterInfo.username}</span>
                {masterInfo.agent_code && <span className="font-mono text-[10px] opacity-70">{masterInfo.agent_code}</span>}
                <button
                  data-testid="master-filter-clear"
                  onClick={clearMasterFilter}
                  className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-violet-200"
                  aria-label={t("ui.clear_filter")}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
              <button
                onClick={() => navigate("/master-agents")}
                className="text-[11px] font-semibold text-[#0F62FE] hover:underline"
              >
                ← {t("ui.back_to_masters")}
              </button>
            </div>
          )}
        </div>
        <button
          data-testid="agent-create-btn"
          onClick={() => { setShowCreate(true); setCreateError(""); }}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6]"
        >
          <Plus className="w-4 h-4" /> {t("agents.create")}
        </button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <span>{t("ui.show")}</span>
            <select
              data-testid="agents-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="h-9 px-2 rounded-md border border-slate-300 bg-white text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            >
              {PAGE_SIZES.map((s) => (<option key={s} value={s}>{s}</option>))}
            </select>
            <span>{t("ui.entries")}</span>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="agents-search"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder={t("ui.search_users")}
              className="h-9 w-full pl-9 pr-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            />
          </div>
        </div>

        <div className="overflow-x-auto overflow-y-visible">
          <table className="w-full text-sm" data-testid="agents-table">
            <thead>
              <tr>
                {["agent_id","name","username","phone","email",...(isAdmin?["master_agent"]:[]),"balance","status","actions"].map((k) => (
                  <th
                    key={k}
                    className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap"
                  >
                    {k === "agent_id" ? "Agent ID"
                      : k === "name" ? t("agents.col_name")
                      : k === "username" ? t("agents.col_username")
                      : k === "phone" ? t("agents.col_phone")
                      : k === "email" ? t("agents.col_email")
                      : k === "master_agent" ? t("ui.master_agent_label")
                      : k === "balance" ? t("agents.col_balance")
                      : k === "status" ? t("agents.col_status")
                      : t("agents.col_actions")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="px-5 py-10 text-center text-sm text-slate-500">
                    {search ? t("common.no_results") : t("agents.no_agents")}
                  </td>
                </tr>
              )}
              {pageRows.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`agent-row-${a.username}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600 font-semibold">{a.agent_code || "—"}</td>
                  <td className="px-4 py-3 text-slate-900 font-semibold">{a.name}</td>
                  <td className="px-4 py-3 text-slate-700">{a.username}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{a.phone || "—"}</td>
                  <td className="px-4 py-3 text-slate-700">{a.email || "—"}</td>
                  {isAdmin && (
                    <td className="px-4 py-3 text-slate-700 text-xs">
                      <span className={
                        "inline-flex px-2 py-0.5 rounded-full font-semibold border " +
                        (a.master_agent?.role === "MASTER_ADMIN"
                          ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-violet-50 text-violet-700 border-violet-200")
                      }>
                        {a.master_agent?.username || "—"}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-emerald-50 text-emerald-700 font-semibold text-xs border border-emerald-200">
                      {formatUSD(a.balance_usd)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <button
                        data-testid={`agent-toggle-${a.username}`}
                        onClick={() => toggleActive(a)}
                        className={
                          "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border " +
                          (a.active
                            ? "bg-emerald-50 text-emerald-700 border-emerald-500"
                            : "bg-slate-100 text-slate-600 border-slate-300")
                        }
                      >
                        {a.active ? t("agents.active") : t("agents.inactive")}
                      </button>
                    ) : (
                      <span
                        data-testid={`agent-status-${a.username}`}
                        className={
                          "inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border " +
                          (a.active
                            ? "bg-emerald-50 text-emerald-700 border-emerald-500"
                            : "bg-slate-100 text-slate-600 border-slate-300")
                        }
                      >
                        {a.active ? t("agents.active") : t("agents.inactive")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <RowActions
                      agent={a}
                      onView={() => openSales(a)}
                      onEdit={() => startEdit(a)}
                      onReset={() => { setResetting(a); setResetPwd(""); }}
                      onTransfer={() => goTransfer(a)}
                      onWithdraw={() => { setWithdrawing(a); setWithdrawAmount(""); }}
                      onTransferCash={() => openTransferCash(a)}
                      onWithdrawCash={() => openWithdrawCash(a)}
                      onSetCommissions={() => navigate(`/set-commissions?user_id=${a.id}`)}
                      onChangeRole={() => setChangingRole(a)}
                      onIdentityDoc={() => setIdDocFor(a)}
                      onToggle={() => toggleActive(a)}
                      isAdmin={isAdmin}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 text-sm">
          <div className="text-slate-500 text-xs">
            {t("ui.showing_range", { from: filtered.length === 0 ? 0 : pageStart + 1, to: Math.min(pageStart + pageSize, filtered.length), total: filtered.length, noun: t("ui.agents_lower") })}
          </div>
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        </div>
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title={t("ui.create_agent_title")} onClose={() => setShowCreate(false)} width="md:max-w-lg">
          <form onSubmit={create} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("ui.full_name")} required>
                <input data-testid="create-agent-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("ui.username")} required>
                <input data-testid="create-agent-username" required value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("ui.email")} required>
                <input data-testid="create-agent-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("ui.password")} required>
                <PasswordInput data-testid="create-agent-password" required minLength={4} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <CountryPhoneFields
              phone={form.phone}
              countryCode={form.country_code}
              onPhoneChange={(v) => setForm({ ...form, phone: v })}
              onCountryChange={(v) => setForm({ ...form, country_code: v })}
              testIdPrefix="create-agent"
            />
            {isAdmin && (
              <Field label={t("ui.assign_master")}>
                <select
                  data-testid="create-agent-master"
                  value={form.master_agent_id || ""}
                  onChange={(e) => setForm({ ...form, master_agent_id: e.target.value || null })}
                  className={inputCls}
                >
                  <option value="">{t("ui.keep_under_me", { admin: currentUser?.username || "admin" })}</option>
                  {masterAgents.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.username} — {m.name} ({m.agents_count ?? 0})
                    </option>
                  ))}
                </select>
              </Field>
            )}
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-[11px] text-blue-700">
              {t("ui.new_agents_hint")}
            </div>
            {createError && (
              <div data-testid="create-agent-error" className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">
                {createError}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setShowCreate(false)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
              <button data-testid="create-agent-submit" disabled={busy} type="submit" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("agents.create")}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit modal */}
      {editing && (
        <Modal title={t("ui.edit_agent_title")} onClose={() => setEditing(null)} width="md:max-w-lg">
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label={t("ui.full_name")}>
                <input data-testid="edit-agent-name" value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("ui.username")}>
                <input
                  data-testid="edit-agent-username"
                  value={editForm.username || ""}
                  onChange={(e) => setEditForm({ ...editForm, username: e.target.value })}
                  autoComplete="off"
                  className={inputCls}
                />
              </Field>
              <Field label={t("ui.email")}>
                <input data-testid="edit-agent-email" type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className={inputCls} />
              </Field>
              <Field label={t("ui.commission_pct")} full>
                <input type="number" step="0.1" min="0" max="100" value={editForm.commission_rate ?? 0} onChange={(e) => setEditForm({ ...editForm, commission_rate: Number(e.target.value) })} className={inputCls} />
              </Field>
            </div>
            <CountryPhoneFields
              phone={editForm.phone}
              countryCode={editForm.country_code}
              onPhoneChange={(v) => setEditForm({ ...editForm, phone: v })}
              onCountryChange={(v) => setEditForm({ ...editForm, country_code: v })}
              testIdPrefix="edit-agent"
            />
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={!!editForm.active} onChange={(e) => setEditForm({ ...editForm, active: e.target.checked })} />
              {t("ui.active_label")}
            </label>
            {editError && (
              <div data-testid="edit-agent-error" className="rounded-md bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2">
                {editError}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditing(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
              <button data-testid="edit-agent-save" onClick={saveEdit} disabled={busy} className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("common.save")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Reset password modal */}
      {resetting && (
        <Modal title={t("ui.reset_password_title", { username: resetting.username })} onClose={() => setResetting(null)}>
          <div className="space-y-3">
            <Field label={t("ui.new_password_min", { defaultValue: "New password (min 8 chars, 1 letter + 1 digit, no spaces)" })}>
              <PasswordInput
                data-testid="reset-password-input"
                value={resetPwd}
                onChange={(e) => setResetPwd(e.target.value)}
                className={inputCls}
                minLength={8}
                autoComplete="new-password"
              />
            </Field>
            <Field label={t("ui.confirm_actor_pw", { defaultValue: "Confirm YOUR password (security check)" })}>
              <PasswordInput
                data-testid="reset-password-actor-input"
                value={resetActorPwd}
                onChange={(e) => setResetActorPwd(e.target.value)}
                className={inputCls}
                autoComplete="current-password"
              />
            </Field>
            <div className="text-xs text-slate-500">
              {t("ui.pw_reset_hint")} {t("ui.pw_reset_hint_iter57", {
                defaultValue: "The agent's active sessions and trusted devices will be revoked; they will need to sign in with the new password and re-verify by SMS.",
              })}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => { setResetting(null); setResetPwd(""); setResetActorPwd(""); }} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
              <button
                data-testid="reset-password-submit"
                onClick={submitReset}
                disabled={busy}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#0F62FE] text-white text-sm font-semibold hover:bg-[#0050E6] disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("auth.reset_password")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Transfer Cash modal */}
      {cashing && (
        <Modal title={t("ui.transfer_cash_title", { username: cashing.username })} onClose={() => setCashing(null)}>
          <div className="space-y-3">
            <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-800">
              <div className="font-semibold flex items-center gap-1.5">
                <Banknote className="w-3.5 h-3.5" /> {t("ui.cash_wallet_hint")}
              </div>
              <div className="mt-1">
                {t("ui.currency")}: <span className="font-bold">{cashTargetWallet?.currency || "—"}</span>
                {cashTargetWallet && (
                  <> · {t("ui.current_balance")}: <span className="font-bold">{Number(cashTargetWallet.balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cashTargetWallet.currency}</span></>
                )}
              </div>
            </div>
            <Field label={t("ui.amount_transfer_in", { currency: cashTargetWallet?.currency || "USD" })}>
              <input
                data-testid="cash-transfer-amount"
                type="number"
                step="0.01"
                min="0"
                value={cashAmount}
                onChange={(e) => setCashAmount(e.target.value)}
                className={inputCls}
                placeholder="0.00"
                autoFocus
              />
            </Field>
            <Field label={t("ui.note_optional")}>
              <input
                data-testid="cash-transfer-note"
                value={cashNote}
                onChange={(e) => setCashNote(e.target.value)}
                className={inputCls}
                placeholder={t("ui.internal_reference")}
                maxLength={300}
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setCashing(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
              <button
                data-testid="cash-transfer-submit"
                onClick={submitTransferCash}
                disabled={busy || !cashTargetWallet}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("ui.transfer_cash_action")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Withdraw Cash modal */}
      {cashWithdrawing && (
        <Modal title={t("ui.withdraw_cash_title", { username: cashWithdrawing.username })} onClose={() => setCashWithdrawing(null)}>
          <div className="space-y-3">
            <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-800">
              <div className="font-semibold flex items-center gap-1.5">
                <HandCoins className="w-3.5 h-3.5" /> {t("ui.pull_cash_hint")}
              </div>
              <div className="mt-1">
                {t("ui.currency")}: <span className="font-bold">{cashWithdrawWallet?.currency || "—"}</span>
                {cashWithdrawWallet && (
                  <> · {t("ui.available")}: <span className="font-bold">{Number(cashWithdrawWallet.balance || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} {cashWithdrawWallet.currency}</span></>
                )}
              </div>
            </div>
            <Field label={t("ui.amount_withdraw_in", { currency: cashWithdrawWallet?.currency || "USD" })}>
              <input
                data-testid="cash-withdraw-amount"
                type="number" step="0.01" min="0" max={cashWithdrawWallet?.balance || 0}
                value={cashWithdrawAmount}
                onChange={(e) => setCashWithdrawAmount(e.target.value)}
                className={inputCls}
                placeholder="0.00"
                autoFocus
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setCashWithdrawing(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
              <button
                data-testid="cash-withdraw-submit"
                onClick={submitWithdrawCash}
                disabled={busy || !cashWithdrawWallet}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("ui.withdraw_cash_action")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Withdraw modal */}
      {withdrawing && (
        <Modal title={t("ui.withdraw_title", { username: withdrawing.username })} onClose={() => setWithdrawing(null)}>
          <div className="space-y-3">
            <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-600">
              {t("ui.agent_current_balance")}: <span className="font-bold text-slate-900">{formatUSD(withdrawing.balance_usd)}</span>
              <br />
              {t("ui.funds_removed_agent_to_master")}
            </div>
            <Field label={t("ui.amount_withdraw_max", { max: formatUSD(withdrawing.balance_usd) })}>
              <input
                data-testid="withdraw-amount"
                type="number"
                step="0.01"
                min="0"
                max={withdrawing.balance_usd}
                value={withdrawAmount}
                onChange={(e) => setWithdrawAmount(e.target.value)}
                className={inputCls}
                placeholder="0.00"
                autoFocus
              />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setWithdrawing(null)} className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50">{t("common.cancel")}</button>
              <button
                data-testid="withdraw-submit"
                onClick={submitWithdraw}
                disabled={busy}
                className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 disabled:opacity-50"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />} {t("ui.withdraw_funds_action")}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* View sales modal */}
      {viewing && (
        <Modal title={t("ui.sales_modal_title", { name: viewing.name, code: viewing.agent_code || viewing.username })} onClose={() => setViewing(null)} width="md:max-w-3xl">
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Mini label={t("agents.col_balance")} value={formatUSD(viewing.balance_usd)} tone="emerald" />
              <Mini label={t("analytics.kpi_period_sales")} value={formatUSD(viewing.sales_total_usd)} tone="blue" />
              <Mini label={t("dashboard.pending_topups") + "/" + t("dashboard.successful_topups")} value={viewing.sales_count ?? 0} tone="violet" />
              <Mini label={t("agents.col_commission")} value={`${viewing.commission_rate}%`} tone="slate" />
            </div>
            <div className="border border-slate-200 rounded-lg max-h-[55vh] overflow-auto">
              <table className="w-full text-xs" data-testid="agent-sales-modal-table">
                <thead className="sticky top-0">
                  <tr>
                    {[t("wallet.col_date"), t("topup.phone"), t("topup.operator"), "USD", t("common.status")].map((h) => (
                      <th key={h} className="bg-slate-50 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider px-3 py-2 border-b border-slate-200">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(viewing.transactions || []).length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">{t("dashboard.no_transactions")}</td></tr>
                  )}
                  {(viewing.transactions || []).map((t) => (
                    <tr key={t.id} className="border-b border-slate-100">
                      <td className="px-3 py-2 text-slate-600">{new Date(t.created_at).toLocaleString("en-US", { dateStyle: "short", timeStyle: "short" })}</td>
                      <td className="px-3 py-2 font-mono">{t.phone_number}</td>
                      <td className="px-3 py-2">{(t.operator_name || "").replace(/\s*\(CLP\)\s*/i, "").trim()}</td>
                      <td className="px-3 py-2 font-semibold">{formatUSD(t.retail_price_usd)}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${
                          t.status === "SUCCESS" ? "bg-emerald-50 text-emerald-700" :
                          t.status === "FAILED" ? "bg-rose-50 text-rose-700" :
                          t.status === "PENDING" ? "bg-amber-50 text-amber-700" :
                          "bg-slate-100 text-slate-600"}`}>
                          {t.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
      )}

      {changingRole && (
        <ChangeRoleModal
          user={changingRole}
          onClose={() => setChangingRole(null)}
          onDone={() => { setChangingRole(null); load(); }}
        />
      )}

      {idDocFor && (
        <IdentityDocumentModal
          userId={idDocFor.id}
          username={idDocFor.username}
          isAdmin={isAdmin}
          onClose={() => setIdDocFor(null)}
        />
      )}
    </div>
  );
}

function blankForm() {
  return { username: "", password: "", name: "", email: "", phone: "", country_code: null, commission_rate: 0, master_agent_id: null };
}

function RowActions({ agent, onView, onEdit, onReset, onTransfer, onWithdraw, onTransferCash, onWithdrawCash, onSetCommissions, onChangeRole, onIdentityDoc, onToggle, isAdmin }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const recompute = () => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuW = 196;
    const left = Math.max(8, Math.min(window.innerWidth - menuW - 8, r.right - menuW));
    const top = r.bottom + 4;
    setPos({ top, left });
  };

  useLayoutEffect(() => {
    if (!open) return;
    recompute();
    const onScroll = () => recompute();
    const onResize = () => recompute();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        data-testid={`agent-actions-${agent.username}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        className="inline-flex items-center gap-1 h-8 px-2 rounded-md border border-[#0F62FE]/30 bg-[#EDF5FF] text-[#0F62FE] hover:bg-[#DCE9FF] text-xs font-semibold"
      >
        <MoreHorizontal className="w-3.5 h-3.5" />
        {t("ui.actions_btn")}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          data-testid={`agent-actions-menu-${agent.username}`}
          style={{ position: "fixed", top: pos.top, left: pos.left, width: 196, zIndex: 9999 }}
          className="rounded-lg border border-slate-200 bg-white shadow-xl py-1 text-sm animate-in fade-in-0 zoom-in-95 duration-100"
        >
          <MenuItem icon={Eye} label={t("ui.view_sales")} onClick={() => { close(); onView(); }} testid={`agent-view-${agent.username}`} tone="primary" />
          <MenuItem icon={Pencil} label={t("ui.edit_agent")} onClick={() => { close(); onEdit(); }} testid={`agent-edit-${agent.username}`} />
          <MenuItem icon={Percent} label={t("ui.set_commissions_action")} onClick={() => { close(); onSetCommissions(); }} testid={`agent-set-commissions-${agent.username}`} tone="primary" />
          <MenuItem icon={KeyRound} label={t("ui.change_password")} onClick={() => { close(); onReset(); }} testid={`agent-reset-${agent.username}`} />
          <MenuItem icon={Send} label={t("ui.transfer_funds_action")} onClick={() => { close(); onTransfer(); }} testid={`agent-transfer-${agent.username}`} />
          {isAdmin && (
            <MenuItem icon={ArrowDown} label={t("ui.withdraw_funds_action")} onClick={() => { close(); onWithdraw(); }} testid={`agent-withdraw-${agent.username}`} />
          )}
          <MenuItem icon={Banknote} label={t("ui.transfer_cash_action")} onClick={() => { close(); onTransferCash(); }} testid={`agent-transfer-cash-${agent.username}`} tone="emerald" />
          {isAdmin && (
            <MenuItem icon={HandCoins} label={t("ui.withdraw_cash_action")} onClick={() => { close(); onWithdrawCash(); }} testid={`agent-withdraw-cash-${agent.username}`} tone="rose" />
          )}
          <MenuItem icon={IdCard} label={t("id_doc.menu_item", "Upload identity document")} onClick={() => { close(); onIdentityDoc(); }} testid={`agent-id-doc-${agent.username}`} tone="primary" />
          {isAdmin && (
            <MenuItem icon={Shuffle} label={t("change_role.menu_item")} onClick={() => { close(); onChangeRole(); }} testid={`agent-change-role-${agent.username}`} tone="primary" />
          )}
          {/* Activate / Deactivate — MASTER_ADMIN or MASTER_AGENT (for their own agents).
              Backend enforces the exact same rule; frontend visibility mirrors it. */}
          <>
            <div className="border-t border-slate-100 my-1" />
            <MenuItem
              icon={Power}
              label={agent.active ? t("agents.deactivate") : t("agents.activate")}
              onClick={() => { close(); onToggle(); }}
              testid={`agent-toggle-menu-${agent.username}`}
              tone={agent.active ? "rose" : "emerald"}
            />
          </>
        </div>,
        document.body,
      )}
    </>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, tone }) {
  const colors = {
    rose: "text-rose-600 hover:bg-rose-50",
    emerald: "text-emerald-700 hover:bg-emerald-50",
    primary: "text-[#0F62FE] hover:bg-blue-50",
  };
  const cls = colors[tone] || "text-slate-700 hover:bg-slate-50 hover:text-[#0F62FE]";
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 ${cls} text-left`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

function Pagination({ page, totalPages, onChange }) {
  const { t } = useTranslation();
  const numbers = [];
  // Show up to 5 numbers around current
  const start = Math.max(1, page - 2);
  const end = Math.min(totalPages, start + 4);
  for (let i = start; i <= end; i++) numbers.push(i);
  const btn = "h-8 min-w-[2rem] px-2 rounded-md border text-sm font-semibold";
  return (
    <div className="flex items-center gap-1" data-testid="agents-pagination">
      <button
        data-testid="agents-prev"
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page <= 1}
        className={`${btn} border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40`}
      >{t("ui.previous")}</button>
      {numbers.map((n) => (
        <button
          key={n}
          data-testid={`agents-page-${n}`}
          onClick={() => onChange(n)}
          className={`${btn} ${n === page ? "bg-[#0F62FE] border-[#0F62FE] text-white" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
        >{n}</button>
      ))}
      <button
        data-testid="agents-next"
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page >= totalPages}
        className={`${btn} border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-40`}
      >{t("ui.next")}</button>
    </div>
  );
}

function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
        {label}{required && <span className="text-rose-500 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function Mini({ label, value, tone = "slate" }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-[#EDF5FF] text-[#0F62FE] border-[#0F62FE]/20",
    violet: "bg-violet-50 text-violet-700 border-violet-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] font-semibold uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold mt-0.5" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</div>
    </div>
  );
}

function Modal({ title, onClose, children, width = "md:max-w-md" }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} />
      <div className={`relative bg-white rounded-xl shadow-xl w-full ${width} p-6 animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-auto`}>
        <button onClick={onClose} className="absolute right-3 top-3 p-1.5 hover:bg-slate-100 rounded-md text-slate-500">
          <X className="w-4 h-4" />
        </button>
        <div className="text-lg font-semibold text-slate-900 mb-4 pr-8" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {title}
        </div>
        {children}
      </div>
    </div>
  );
}
